import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, forwardRef, useId } from 'react';
import { theme } from './theme.js';
import { css, inputReset, inputBase, inputFocusRing, inputErrorRing } from './styles.js';
export const Textarea = forwardRef(({ value, onChange, placeholder, rows = 4, maxLength, size = 'md', label, error, helpText, disabled = false, resize = 'vertical', className, style, name, required, }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const id = useId();
    const sizeStyles = {
        sm: {
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            fontSize: theme.typography.fontSize.sm.size,
        },
        md: {
            padding: `${theme.spacing.md} ${theme.spacing.lg}`,
            fontSize: theme.typography.fontSize.base.size,
        },
        lg: {
            padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
            fontSize: theme.typography.fontSize.lg.size,
        },
    };
    const sizeConfig = sizeStyles[size];
    const resizeMap = {
        none: 'none',
        vertical: 'vertical',
        both: 'both',
    };
    const textareaStyle = css(inputReset(), inputBase(), {
        ...sizeConfig,
        border: `1.5px solid ${isFocused ? 'var(--k-brand)' : error ? 'var(--k-danger)' : 'var(--k-border)'}`,
        fontFamily: theme.typography.fontFamily.primary,
        fontWeight: theme.typography.fontWeight.normal,
        lineHeight: theme.typography.fontSize.base.lineHeight,
        resize: resizeMap[resize],
        minHeight: '80px',
        boxSizing: 'border-box',
    }, isFocused ? inputFocusRing() : undefined, !isFocused && error ? inputErrorRing() : undefined, disabled
        ? {
            opacity: 0.6,
            cursor: 'not-allowed',
            backgroundColor: 'var(--k-bg-elevated)',
        }
        : undefined, style);
    const containerStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.xs,
    };
    const labelStyle = {
        fontSize: theme.typography.fontSize.sm.size,
        fontWeight: theme.typography.fontWeight.medium,
        color: 'var(--k-text-primary)',
        margin: 0,
    };
    const footerRowStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: theme.spacing.sm,
        marginTop: theme.spacing.xs,
    };
    const errorStyle = {
        fontSize: theme.typography.fontSize.xs.size,
        color: 'var(--k-danger)',
        margin: 0,
    };
    const helpStyle = {
        fontSize: theme.typography.fontSize.xs.size,
        color: 'var(--k-text-secondary)',
        margin: 0,
    };
    const charCountStyle = {
        fontSize: theme.typography.fontSize.xs.size,
        color: maxLength && typeof value === 'string' && value.length >= maxLength
            ? 'var(--k-danger)'
            : 'var(--k-text-muted)',
        marginTop: 0,
        marginRight: 0,
        marginBottom: 0,
        marginLeft: 'auto',
        whiteSpace: 'nowrap',
    };
    const charCount = typeof value === 'string' ? value.length : 0;
    const showFooter = error || (helpText && !error) || maxLength;
    return (_jsxs("div", { style: containerStyle, className: className, children: [label && (_jsxs("label", { htmlFor: id, style: labelStyle, children: [label, required && (_jsx("span", { style: { color: 'var(--k-danger)', marginLeft: theme.spacing.xs }, children: "*" }))] })), _jsx("textarea", { ref: ref, id: id, name: name, rows: rows, maxLength: maxLength, placeholder: placeholder, value: value, onChange: (e) => onChange?.(e.target.value), disabled: disabled, required: required, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), style: textareaStyle, "aria-invalid": !!error, "aria-describedby": error ? `${id}-error` : helpText ? `${id}-help` : undefined }), showFooter && (_jsxs("div", { style: footerRowStyle, children: [error ? (_jsx("p", { id: `${id}-error`, style: errorStyle, role: "alert", children: error })) : helpText ? (_jsx("p", { id: `${id}-help`, style: helpStyle, children: helpText })) : (_jsx("span", {})), maxLength && (_jsxs("span", { style: charCountStyle, "aria-live": "polite", children: [charCount, "/", maxLength] }))] }))] }));
});
Textarea.displayName = 'Textarea';
//# sourceMappingURL=Textarea.js.map